import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, X } from "lucide-react";
import { useSite } from "@/lib/SiteContext";
import { useT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  organization: "",
  territory: "",
  category: "experiencia",
  description: "",
  website: "",
};

export const ReferralDialog = () => {
  const { referralOpen, closeReferral, locale } = useSite();
  const { t, d } = useT(locale);
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    if (referralOpen) {
      setForm(initial);
      setStatus("idle");
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [referralOpen]);

  if (!referralOpen) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/referral`, { ...form, locale });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const categoryOptions = d.forms.category_options;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end md:items-center justify-center"
      role="dialog"
      aria-modal="true"
      data-testid="referral-dialog"
    >
      <button
        className="absolute inset-0 bg-[var(--ix-charcoal)]/55 backdrop-blur-sm"
        onClick={closeReferral}
        aria-label="Close"
      />
      <div className="relative bg-[var(--ix-cream)] w-full md:max-w-2xl md:rounded-sm border border-[var(--ix-line-light)] shadow-2xl max-h-[92vh] overflow-y-auto" style={{ color: "var(--ix-night)" }}>
        <div className="px-6 md:px-10 py-6 flex items-start justify-between border-b border-[var(--ix-line-light)] sticky top-0 bg-[var(--ix-cream)] z-10">
          <div>
            <div className="ix-mono text-[0.66rem] uppercase tracking-[0.22em] ix-grad-text font-semibold">
              {locale === "es" ? "Aliado referido" : "Referred ally"}
            </div>
            <h3 className="ix-display text-[1.7rem] md:text-[2rem] mt-2 text-[var(--ix-night)] leading-tight">
              {t("forms.referral_title")}
            </h3>
            <p className="text-[0.88rem] text-[var(--ix-muted)] mt-2 max-w-md">
              {t("forms.referral_sub")}
            </p>
          </div>
          <button
            onClick={closeReferral}
            className="p-2 -mt-1 -mr-2 hover:bg-[var(--ix-sand)] transition-colors"
            aria-label="Close"
            data-testid="referral-close"
          >
            <X size={20} />
          </button>
        </div>

        {status === "success" ? (
          <div className="px-6 md:px-10 py-12 text-center" data-testid="referral-success">
            <div className="ix-display text-[2rem] ix-grad-text">
              {locale === "es" ? "Recibido." : "Received."}
            </div>
            <p className="mt-3 text-[var(--ix-muted)] max-w-md mx-auto">
              {t("forms.success_referral")}
            </p>
            <button onClick={closeReferral} className="ix-btn ix-btn-grad mt-7">
              {locale === "es" ? "Cerrar" : "Close"}
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="px-6 md:px-10 py-7 space-y-5" data-testid="referral-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label={t("forms.name")} required>
                <input required value={form.name} onChange={update("name")} className="ix-input" data-testid="referral-name" />
              </Field>
              <Field label={t("forms.email")} required>
                <input required type="email" value={form.email} onChange={update("email")} className="ix-input" data-testid="referral-email" />
              </Field>
              <Field label={t("forms.phone")}>
                <input type="tel" value={form.phone} onChange={update("phone")} className="ix-input" data-testid="referral-phone" />
              </Field>
              <Field label={t("forms.organization")}>
                <input value={form.organization} onChange={update("organization")} className="ix-input" data-testid="referral-org" />
              </Field>
              <Field label={t("forms.territory")}>
                <input value={form.territory} onChange={update("territory")} className="ix-input" data-testid="referral-territory" />
              </Field>
              <Field label={t("forms.website")}>
                <input value={form.website} onChange={update("website")} className="ix-input" data-testid="referral-website" placeholder="https://" />
              </Field>
            </div>

            <Field label={t("forms.category")} required>
              <div className="flex flex-wrap gap-2">
                {Object.entries(categoryOptions).map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, category: k }))}
                    className={`px-3 py-1.5 text-[0.82rem] rounded-full border transition-all ${
                      form.category === k
                        ? "bg-[var(--ix-night)] text-[var(--ix-cream)] border-[var(--ix-night)]"
                        : "bg-transparent text-[var(--ix-night)]/75 border-[var(--ix-line-light)] hover:border-[var(--ix-night)]"
                    }`}
                    data-testid={`referral-category-${k}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </Field>

            <Field label={t("forms.description")} required>
              <textarea
                required
                rows={4}
                value={form.description}
                onChange={update("description")}
                className="ix-input resize-none"
                data-testid="referral-description"
              />
            </Field>

            {status === "error" && (
              <div className="text-[0.88rem] text-[#C45D3A]" data-testid="referral-error">
                {t("forms.error")}
              </div>
            )}

            <div className="flex justify-end pt-3">
              <button type="submit" disabled={status === "sending"} className="ix-btn ix-btn-grad" data-testid="referral-submit">
                {status === "sending" ? (
                  <>
                    <Loader2 className="animate-spin" size={15} />
                    {t("forms.sending")}
                  </>
                ) : (
                  t("forms.submit")
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="ix-mono text-[0.65rem] uppercase tracking-[0.18em] text-[var(--ix-night)]/65 block mb-2">
      {label} {required ? <span className="ix-grad-text font-bold">*</span> : null}
    </span>
    {children}
  </label>
);

export default ReferralDialog;
